import React, { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import ShopProductGrid from "../components/product/ShopProductGrid";
import ProductFilters from "../components/product/ProductFilters";
import Seo from "../components/seo/Seo";
import {
  categoryList,
  getCategoryBySlug,
  getProductsByCategorySlug,
} from "../lib/catalog";
import { filterProducts } from "../utils/productFilters";

const toggleSelection = (current, value) =>
  current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];

const CategoryPage = () => {
  const { slug } = useParams();
  const category = getCategoryBySlug(slug || "");
  const [productTypes, setProductTypes] = useState([]);
  const [areas, setAreas] = useState([]);

  const categoryProducts = useMemo(
    () => (category ? getProductsByCategorySlug(category.slug) : []),
    [category],
  );

  const filteredProducts = useMemo(
    () =>
      filterProducts(categoryProducts, {
        productTypes,
        areas,
      }),
    [categoryProducts, productTypes, areas],
  );

  const clearAllFilters = () => {
    setProductTypes([]);
    setAreas([]);
  };

  if (!category) {
    return <Navigate to="/collections" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black py-12">
      <Seo
        title={category.title}
        description={category.description}
        path={`/category/${category.slug}`}
      />

      <div className="container mx-auto px-6">
        <div className="mb-8 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <div className="text-sm text-gray-400">
            <Link to="/collections" className="hover:text-amber-300">
              Collections
            </Link>
          </div>
          <span className="text-gray-500" aria-hidden="true">
            /
          </span>
          <h1 className="text-2xl font-light tracking-tight text-white md:text-3xl">
            {category.title}
          </h1>
        </div>

        <div className="mb-8 flex flex-wrap gap-3">
          {categoryList.map((item) => (
            <Link
              key={item.slug}
              to={`/category/${item.slug}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                item.slug === category.slug
                  ? "border-amber-400 bg-amber-400 text-black"
                  : "border-white/10 bg-white/5 text-gray-200 hover:border-amber-400/30 hover:text-white"
              }`}
            >
              {item.title}
            </Link>
          ))}
        </div>

        <ProductFilters
          productTypes={productTypes}
          areas={areas}
          onToggleProductType={(id) =>
            setProductTypes((current) => toggleSelection(current, id))
          }
          onToggleArea={(id) =>
            setAreas((current) => toggleSelection(current, id))
          }
          onClearAll={clearAllFilters}
          productList={categoryProducts}
          resultCount={filteredProducts.length}
          totalCount={categoryProducts.length}
        />

        {filteredProducts.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-16 text-center">
            <p className="text-lg text-white">No products match your filters</p>
            <button
              type="button"
              onClick={clearAllFilters}
              className="mt-6 rounded-full border border-amber-400/40 px-5 py-2 text-sm text-amber-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <ShopProductGrid products={filteredProducts} />
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
